import React, { useState } from 'react';
import { ModuleCard } from './ModuleCard';
import { ModuleOrdersOverview } from './ModuleOrdersOverview';
import { RecipeManagement } from './RecipeManagement';
import { OrderPlanning } from './OrderPlanning';
import { MaterialPreparation } from './MaterialPreparation';
import { TKLinesModule } from './TKLinesModule';
import { WeighingOverview } from './WeighingOverview';
import { TKStorageModule } from './TKStorageModule';
import { FlourOilWeighing } from './FlourOilWeighing';
import { SpiceWeighing } from './SpiceWeighing';
import { SpiceStorage } from './SpiceStorage';
import { MixerModule } from './MixerModule';
import { MultiheadWeigherModule } from './MultiheadWeigherModule';
import { CookingSystemModule } from './CookingSystemModule';
import { TraceabilityModule } from './TraceabilityModule';
import { PackageIcon, TruckIcon, CalendarIcon, ScaleIcon, FlaskConicalIcon, WarehouseIcon, CookingPotIcon, FactoryIcon, ClipboardListIcon, LayersIcon, GaugeIcon, ShieldIcon, HistoryIcon, LockIcon, XIcon, CheckIcon } from 'lucide-react';
import { User } from './UserManagement';
interface ModuleOverviewProps {
  currentUser: User | null;
}
export function ModuleOverview({
  currentUser
}: ModuleOverviewProps) {
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showOrdersOverview, setShowOrdersOverview] = useState(false);
  const [passwordModal, setPasswordModal] = useState<{
    show: boolean;
    module: string | null;
  }>({
    show: false,
    module: null
  });
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  // List of modules that don't require password
  const noPasswordModules = ['Stammdaten', 'Bereitstellung', 'TK Linien', 'Rückverfolgung'];
  const handleModuleClick = (moduleName: string) => {
    // Check if user has permission for this module
    if (currentUser && currentUser.username !== 'admin' && currentUser.permissions && !currentUser.permissions.includes(moduleName)) {
      // Show a permission denied alert
      alert('Sie haben keine Berechtigung für dieses Modul.');
      return;
    }
    // Check if module needs password protection
    if (!noPasswordModules.includes(moduleName)) {
      setPasswordModal({
        show: true,
        module: moduleName
      });
      return;
    }
    // Special case for Mischer, Mehrkopfwaage, and Kochanlage modules - directly show the module without orders overview
    if (moduleName === 'Mischer' || moduleName === 'Mehrkopfwaage' || moduleName === 'Kochanlage') {
      setActiveModule(moduleName);
      setShowOrdersOverview(false);
      return;
    }
    const moduleWithOrders = allModules.find(m => m.id === moduleName && m.orders && m.orders.length > 0);
    if (moduleWithOrders) {
      setActiveModule(moduleName);
      setShowOrdersOverview(true);
    } else {
      setActiveModule(moduleName);
      setShowOrdersOverview(false);
    }
  };
  const handlePasswordSubmit = () => {
    if (password === '123') {
      setPasswordModal({
        show: false,
        module: null
      });
      setPassword('');
      setPasswordError(false);
      // Continue with module activation
      if (passwordModal.module) {
        const moduleWithOrders = allModules.find(m => m.id === passwordModal.module && m.orders && m.orders.length > 0);
        if (moduleWithOrders) {
          setActiveModule(passwordModal.module);
          setShowOrdersOverview(true);
        } else {
          setActiveModule(passwordModal.module);
          setShowOrdersOverview(false);
        }
      }
    } else {
      setPasswordError(true);
    }
  };
  const handlePasswordCancel = () => {
    setPasswordModal({
      show: false,
      module: null
    });
    setPassword('');
    setPasswordError(false);
  };
  const handleOrderSelect = (orderId: string) => {
    setSelectedOrderId(orderId);
    setShowOrdersOverview(false);
    // You can add logic here to determine which module to activate based on the order
    const moduleForOrder = allModules.find(module => module.orders && module.orders.some(order => order.id === orderId));
    if (moduleForOrder) {
      setActiveModule(moduleForOrder.id);
    }
  };
  const handleBackToOverview = () => {
    setActiveModule(null);
    setSelectedOrderId(null);
    setShowOrdersOverview(false);
  };
  const handleBackToOrdersOverview = () => {
    setShowOrdersOverview(true);
  };
  // Sample orders for production modules with deadlines
  const now = new Date();
  const mixerOrders = [{
    id: 'M1001',
    name: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(now.getTime() + 60 * 60000) // 1 hour from now
  }, {
    id: 'M1002',
    name: 'Gustoria Risotto',
    status: 'in-progress',
    deadline: new Date(now.getTime() + 30 * 60000) // 30 minutes from now
  }, {
    id: 'M1003',
    name: 'Gustoria Bolognese',
    status: 'completed',
    deadline: new Date(now.getTime() - 30 * 60000) // 30 minutes ago
  }];
  const multiheadWeigherOrders = [{
    id: 'W1001',
    name: 'Gustoria Paella',
    status: 'in-progress',
    deadline: new Date(now.getTime() + 45 * 60000) // 45 minutes from now
  }, {
    id: 'W1002',
    name: 'Gustoria Risotto',
    status: 'pending',
    deadline: new Date(now.getTime() + 120 * 60000) // 2 hours from now
  }];
  const cookingSystemOrders = [{
    id: 'C1001',
    name: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(now.getTime() + 90 * 60000) // 1.5 hours from now
  }, {
    id: 'C1002',
    name: 'Gustoria Bolognese',
    status: 'pending',
    deadline: new Date(now.getTime() + 150 * 60000) // 2.5 hours from now
  }];
  // Alle Module in einem Array zusammenfassen
  const allModules = [
  // Hauptmodule
  {
    id: 'Auftragsplanung',
    title: 'Auftragsplanung',
    icon: <CalendarIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-879" />,
    description: 'Planung und Verwaltung von Produktionsaufträgen',
    status: 'active'
  }, {
    id: 'Stammdaten',
    title: 'Stammdaten',
    icon: <ClipboardListIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-880" />,
    description: 'Verwaltung von Rezepten und Produktspezifikationen',
    status: 'active'
  }, {
    id: 'Bereitstellung',
    title: 'Bereitstellung',
    icon: <PackageIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-881" />,
    description: 'Materialbereitstellung und Vorbereitung für die Produktion',
    status: 'active'
  }, {
    id: 'TK Linien',
    title: 'TK Linien',
    icon: <TruckIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-882" />,
    description: 'Überwachung und Steuerung der Tiefkühllinien',
    status: 'active'
  }, {
    id: 'TK-Lager',
    title: 'TK-Lager',
    icon: <WarehouseIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-883" />,
    description: 'Verwaltung und Überwachung des Tiefkühllagers',
    status: 'active'
  }, {
    id: 'Rückverfolgung',
    title: 'Rückverfolgung',
    icon: <HistoryIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-884" />,
    description: 'Rückverfolgung von Rohstoffen und Produktionsprozessen',
    status: 'active'
  }, {
    id: 'Verwiegung',
    title: 'Verwiegung',
    icon: <ScaleIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-885" />,
    description: 'Verwiegung und Kontrolle von Rohstoffen',
    status: 'active'
  },
  // Material & Vorbereitung
  {
    id: 'Mehl und Öl Verwiegung',
    title: 'Mehl und Öl Verwiegung',
    icon: <ScaleIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-886" />,
    description: 'Präzise Verwiegung von Mehl und Öl für die Produktion',
    status: 'active'
  }, {
    id: 'Gewürzverwiegung',
    title: 'Gewürzverwiegung',
    icon: <FlaskConicalIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-887" />,
    description: 'Exakte Dosierung und Verwiegung von Gewürzen',
    status: 'active'
  },
  // Lagerverwaltung
  {
    id: 'Gewürzlager',
    title: 'Gewürzlager',
    icon: <LayersIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-888" />,
    description: 'Bestand und Verwaltung der Gewürzkomponenten',
    status: 'inactive'
  },
  // Produktionsmodule - Removed "Produktion"
  {
    id: 'Mischer',
    title: 'Mischer',
    icon: <CookingPotIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-889" />,
    description: 'Steuerung und Überwachung der Mischprozesse',
    status: 'inactive',
    orders: mixerOrders
  }, {
    id: 'Mehrkopfwaage',
    title: 'Mehrkopfwaage',
    icon: <ScaleIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-890" />,
    description: 'Präzise Verwiegung mit der Mehrkopfwaage',
    status: 'active',
    orders: multiheadWeigherOrders
  }, {
    id: 'Kochanlage',
    title: 'Kochanlage',
    icon: <CookingPotIcon className="w-7 h-7 text-[#8DC63F]" data-id="element-891" />,
    description: 'Steuerung und Überwachung der Kochprozesse',
    status: 'inactive',
    orders: cookingSystemOrders
  }];
  // Filter modules based on user permissions (admin sees all)
  const filteredModules = currentUser?.username === 'admin' ? allModules : allModules.filter(module => !currentUser?.permissions || currentUser.permissions.includes(module.id));
  // Show orders overview for a specific module
  if (showOrdersOverview && activeModule) {
    const currentModule = allModules.find(m => m.id === activeModule);
    if (currentModule && currentModule.orders) {
      return <ModuleOrdersOverview moduleName={currentModule.title} orders={currentModule.orders} onOrderSelect={handleOrderSelect} onBackToOverview={handleBackToOverview} data-id="element-892" />;
    }
  }
  // Show specific module views
  if (activeModule === 'Stammdaten') {
    return <RecipeManagement onBackToOverview={handleBackToOverview} data-id="element-893" />;
  }
  if (activeModule === 'Auftragsplanung') {
    return <OrderPlanning onBackToOverview={handleBackToOverview} data-id="element-894" />;
  }
  if (activeModule === 'Bereitstellung') {
    return <MaterialPreparation onBackToOverview={handleBackToOverview} data-id="element-895" />;
  }
  if (activeModule === 'TK Linien') {
    return <TKLinesModule onBackToOverview={handleBackToOverview} data-id="element-896" />;
  }
  if (activeModule === 'Verwiegung') {
    return <WeighingOverview onBackToOverview={handleBackToOverview} data-id="element-897" />;
  }
  if (activeModule === 'TK-Lager') {
    return <TKStorageModule onBackToOverview={handleBackToOverview} data-id="element-898" />;
  }
  if (activeModule === 'Mehl und Öl Verwiegung') {
    return <FlourOilWeighing onBackToOverview={handleBackToOverview} data-id="element-899" />;
  }
  if (activeModule === 'Gewürzverwiegung') {
    return <SpiceWeighing onBackToOverview={handleBackToOverview} data-id="element-900" />;
  }
  if (activeModule === 'Gewürzlager') {
    return <SpiceStorage onBackToOverview={handleBackToOverview} data-id="element-901" />;
  }
  if (activeModule === 'Mehrkopfwaage') {
    return <MultiheadWeigherModule onBackToOverview={handleBackToOverview} data-id="element-902" />;
  }
  if (activeModule === 'Kochanlage') {
    return <CookingSystemModule onBackToOverview={handleBackToOverview} data-id="element-903" />;
  }
  if (activeModule === 'Mischer') {
    return <MixerModule onBackToOverview={handleBackToOverview} data-id="element-904" />;
  }
  if (activeModule === 'Rückverfolgung') {
    return <TraceabilityModule onBackToOverview={handleBackToOverview} data-id="element-905" />;
  }
  return <div className="container mx-auto px-4" data-id="element-906">
      {/* Password Modal */}
      {passwordModal.show && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-907">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" data-id="element-908">
            <div className="flex justify-between items-center mb-4" data-id="element-909">
              <h3 className="text-xl font-bold text-gray-800" data-id="element-910">
                Passwortschutz
              </h3>
              <button onClick={handlePasswordCancel} className="text-gray-500 hover:text-gray-700" data-id="element-911">
                <XIcon size={20} data-id="element-912" />
              </button>
            </div>
            <div className="mb-6" data-id="element-913">
              <div className="flex items-center justify-center mb-4" data-id="element-914">
                <div className="bg-blue-100 p-3 rounded-full" data-id="element-915">
                  <LockIcon size={24} className="text-blue-600" data-id="element-916" />
                </div>
              </div>
              <p className="text-center text-gray-600 mb-4" data-id="element-917">
                Bitte geben Sie das Passwort ein, um auf das Modul "
                {allModules.find(m => m.id === passwordModal.module)?.title}"
                zuzugreifen.
              </p>
              <div className="mb-4" data-id="element-918">
                <input type="password" className={`w-full px-4 py-2 border ${passwordError ? 'border-red-500' : 'border-gray-300'} rounded-md`} placeholder="Passwort eingeben" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()} data-id="element-919" />
                {passwordError && <p className="text-red-500 text-sm mt-1" data-id="element-920">
                    Falsches Passwort. Bitte versuchen Sie es erneut.
                  </p>}
              </div>
            </div>
            <div className="flex gap-3" data-id="element-921">
              <button onClick={handlePasswordCancel} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md" data-id="element-922">
                Abbrechen
              </button>
              <button onClick={handlePasswordSubmit} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center" data-id="element-923">
                <CheckIcon size={18} className="mr-2" data-id="element-924" />
                Bestätigen
              </button>
            </div>
          </div>
        </div>}

      <div className="mb-8 max-w-3xl" data-id="element-925">
        <h2 className="text-3xl font-bold text-gray-800 mb-3" data-id="element-926">
          Produktionssteuerung
        </h2>
        <p className="text-xl text-gray-600" data-id="element-927">
          Willkommen im Produktionssteuerungssystem. Wählen Sie ein Modul aus,
          um mit der Arbeit zu beginnen.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100" data-id="element-928">
        {/* First row with the specified modules */}
        <div className="mb-8" data-id="element-929">
          <h3 className="text-lg font-semibold text-gray-700 mb-4" data-id="element-930">
            Hauptmodule
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-id="element-931">
            {filteredModules.filter(module => ['Stammdaten', 'Bereitstellung', 'TK Linien', 'Rückverfolgung'].includes(module.id)).map(module => <div key={module.id} onClick={() => handleModuleClick(module.id)} data-id="element-932">
                  <ModuleCard title={module.title} icon={module.icon} description={module.description} status={module.status} compact={false} data-id="element-933" />
                </div>)}
          </div>
        </div>

        {/* Other modules */}
        <div data-id="element-934">
          <h3 className="text-lg font-semibold text-gray-700 mb-4" data-id="element-935">
            Weitere Module
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5" data-id="element-936">
            {filteredModules.filter(module => !['Stammdaten', 'Bereitstellung', 'TK Linien', 'Rückverfolgung'].includes(module.id)).map(module => <div key={module.id} onClick={() => handleModuleClick(module.id)} data-id="element-937">
                  <ModuleCard title={module.title} icon={module.icon} description={module.description} status={module.status} compact={false} locked={!noPasswordModules.includes(module.id)} data-id="element-938" />
                </div>)}
          </div>
        </div>
      </div>
    </div>;
}